import { useSignalR } from '../components/providers/SignalRProvider.vue'
import type { SignalRReceiveEvent, SubscribeToEventOptions } from '../store'
import { ref } from 'vue'
import type {
  CompanyAddedSignalRPayload,
  CompanyDetailsChangedSignalRPayload,
  CompanyDeactivatedSignalRPayload,
  CompanyReactivatedSignalRPayload
} from '@gat-ship/types/signal-r-events'
import { signalREvent } from '@gat-ship/types/signal-r-events'

export type CompanyEventHandlers = {
  onCompanyAdded?: (
    payload: SignalRReceiveEvent<CompanyAddedSignalRPayload>
  ) => void
  onCompanyDetailsChanged?: (
    payload: SignalRReceiveEvent<CompanyDetailsChangedSignalRPayload>
  ) => void
  onCompanyDeactivated?: (
    payload: SignalRReceiveEvent<CompanyDeactivatedSignalRPayload>
  ) => void
  onCompanyReactivated?: (
    payload: SignalRReceiveEvent<CompanyReactivatedSignalRPayload>
  ) => void
}

export function useCompanySignalREvents(handlers?: CompanyEventHandlers) {
  const { subscribe } = useSignalR()

  const subscription = ref<() => void>()

  function subscribeToCompanyAdded(
    cb: (payload: SignalRReceiveEvent<CompanyAddedSignalRPayload>) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<CompanyAddedSignalRPayload>(
      signalREvent.Organization.V1.CompanyAdded,
      cb,
      options
    )
  }

  function subscribeToCompanyDetailsChanged(
    cb: (
      payload: SignalRReceiveEvent<CompanyDetailsChangedSignalRPayload>
    ) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<CompanyDetailsChangedSignalRPayload>(
      signalREvent.Organization.V1.CompanyDetailsChanged,
      cb,
      options
    )
  }

  function subscribeToCompanyDeactivated(
    cb: (
      payload: SignalRReceiveEvent<CompanyDeactivatedSignalRPayload>
    ) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<CompanyDeactivatedSignalRPayload>(
      signalREvent.Organization.V1.CompanyDeactivated,
      cb,
      options
    )
  }

  function subscribeToCompanyReactivated(
    cb: (
      payload: SignalRReceiveEvent<CompanyReactivatedSignalRPayload>
    ) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<CompanyReactivatedSignalRPayload>(
      signalREvent.Organization.V1.CompanyReactivated,
      cb,
      options
    )
  }

  function subscribeToCompanyEvents() {
    if (subscription.value) {
      return subscription.value
    }

    if (handlers) {
      const unsubscribeFunctions: (() => void)[] = []

      if (handlers.onCompanyAdded) {
        unsubscribeFunctions.push(
          subscribeToCompanyAdded(handlers.onCompanyAdded)
        )
      }
      if (handlers.onCompanyDetailsChanged) {
        unsubscribeFunctions.push(
          subscribeToCompanyDetailsChanged(handlers.onCompanyDetailsChanged)
        )
      }
      if (handlers.onCompanyDeactivated) {
        unsubscribeFunctions.push(
          subscribeToCompanyDeactivated(handlers.onCompanyDeactivated)
        )
      }
      if (handlers.onCompanyReactivated) {
        unsubscribeFunctions.push(
          subscribeToCompanyReactivated(handlers.onCompanyReactivated)
        )
      }

      subscription.value = () => {
        unsubscribeFunctions.forEach((unsubscribe) => unsubscribe())
      }
    }

    return subscription.value
  }

  function unsubscribeFromCompanyEvents() {
    subscription.value?.()
    subscription.value = undefined
  }

  return {
    subscribeToCompanyEvents,
    unsubscribeFromCompanyEvents
  }
}
